/**
 * A promised wrapper around window.setTimeout() - nothing fancy
 * @param {number} ms The delay in ms
 * @returns {Promise<void>} A promise when the delay is complete
 */
export function promisedDelay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isPromise<T = any>(promise: any): promise is Promise<T> {
    return (
        !!promise &&
        (typeof promise === 'object' || typeof promise === 'function') &&
        typeof promise.then === 'function'
    );
}

export class Defer<T = unknown> {
    _promise: Promise<T>;
    _resolve: (value: T | PromiseLike<T>) => void;
    _reject: (reason?: any) => void;

    /**
     * Creates a deferred object out of an ES6 Promise.
     * NOTE This is considered an anti-pattern because you lose throw-safety.
     * Only use if you have to
     *
     * Example Usage:
     * 1. create a defer object and store it away
     * 2. return the defer.promise for the caller to use as a normal promise
     * 3. at some arbitrary point, whip out that defer object and call defer.resolve() or defer.reject()
     */
    constructor() {
        this._promise = new Promise((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
    }

    public get resolve(): (value?: T | PromiseLike<T>) => void {
        return this._resolve;
    }

    public get reject(): (reason?: any) => void {
        return this._reject;
    }

    public get promise(): Promise<T> {
        return this._promise;
    }
}

/**
 * I'm sorry this function exists, but I too often end up writing code which has a try catch block
 *  around too many lines of code that I don't actually want to catch any errors,
 *  simply because I don't like how many lines of code it takes to do it properly;
 * So now I use this.
 *
 * @example
 * ----- what I usually write -------
 * try {
 *     const value = await doThing();
 *     doThingWithValue(value);
 * } catch (error) {
 *     return error;
 * }
 * ----- what I should be writing -------
 * let value;
 * try {
 *     value = await doThing();
 * } catch (error) {
 *     return error;
 * }
 * doThingWithValue(value);
 * ----- what I now write  -------
 * const [value, error] = catchPromiseAndReturnError(doThing());
 * if (error) {
 *     return error;
 * }
 * doThingWithValue(value);
 */
export async function catchPromiseAndReturnError<T = unknown, ErrorType extends Error = Error>(
    promise: Promise<T>,
): Promise<[T, ErrorType | undefined]> {
    if (!isPromise(promise)) {
        throw new Error('expected a promise!');
    }
    let value = undefined,
        error = undefined;
    try {
        value = await promise;
    } catch (e) {
        error = e;
    }
    return [value, error];
}

/**
 * Very similar to above except it takes an invoker instead of a promise directly.
 *  This ensures a promise will always be returned instead of potentially an error being thrown before it can be caught.
 *
 *  For example
 *
 *  const {} = await settlePromise(() => throw Error());
 *
 *  as opposed to
 *
 *  const [] = await catchPromiseAndReturnError(throw Error());
 *
 *  the latter will NOT catch the error for you, and in fact you need to wrap it in a try-catch to catch the
 *   thrown error... the former example (this function) doesn't suffer from that, because of the invoker interface
 */
export async function settlePromise<T = unknown>(
    invoker: () => Promise<T>,
): Promise<PromiseSettledResult<T>> {
    const p = new Promise<T>((res, rej) => {
        const promise = invoker();
        promise.then(res).catch(rej);
    });
    const [result] = await Promise.allSettled([p]);
    return result;
}
